import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft, BadgeCheck, Ban, Lock, Mail, Plus, Send, Trash2, Undo2, X, AlertTriangle, ExternalLink,
} from 'lucide-react';
import { usePayrollRun, usePayrollRuns, useRunPayslipEditor } from '@/composables/payroll/usePayrollRuns';
import { useAccounts } from '@/composables/financial/useAccounts';
import { PageContainer } from '@/components/layout/PageContainer';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DecimalInput } from '@/components/forms/DecimalInput';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { EmptyState } from '@/components/EmptyState';
import { formatCurrency, formatDateUTC } from '@/lib/formatters';
import { MONTH_OPTIONS, PAYROLL_RUN_STATUS_STYLES, type Payslip } from '@/types/payroll';

export function PayrollRunDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { run, isLoading } = usePayrollRun(id ?? '');
  const { submit, reopen, approve, markPaid, cancel, emailAll } = usePayrollRuns();
  const { updatePayslip, addAdjustment, removeAdjustment, excludePayslip } = useRunPayslipEditor(id ?? '');

  const [approveOpen, setApproveOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [adjustTarget, setAdjustTarget] = useState<Payslip | null>(null);
  const [excludeTarget, setExcludeTarget] = useState<Payslip | null>(null);

  if (isLoading) return <LoadingSpinner />;
  if (!run) return <EmptyState title="Payroll run not found" description="It may have been deleted." />;

  const fmt = (v: string | number) => formatCurrency(v, run.currency);
  const isDraft = run.status === 'DRAFT';
  const payslips = run.payslips ?? [];
  const periodLabel = `${MONTH_OPTIONS[run.periodMonth - 1]?.label} ${run.periodYear}`;

  return (
    <PageContainer backTo
      title={run.runNumber}
      description={`${periodLabel} · ${run.employeeCount} employee${run.employeeCount === 1 ? '' : 's'}`}
      actions={
        <>
          <Button variant="outline" onClick={() => navigate('/people/payroll-runs')}>
            <ArrowLeft className="mr-2 h-4 w-4" />Back
          </Button>
          {isDraft && (
            <Button onClick={() => submit.mutate(run.id)} disabled={submit.isPending || payslips.length === 0}>
              <Send className="mr-2 h-4 w-4" />{submit.isPending ? 'Submitting…' : 'Send for review'}
            </Button>
          )}
          {run.status === 'REVIEW' && (
            <>
              <Button variant="outline" onClick={() => reopen.mutate(run.id)} disabled={reopen.isPending}>
                <Undo2 className="mr-2 h-4 w-4" />Reopen
              </Button>
              <Button onClick={() => setApproveOpen(true)}>
                <BadgeCheck className="mr-2 h-4 w-4" />Approve &amp; post
              </Button>
            </>
          )}
          {(run.status === 'APPROVED' || run.status === 'PAID') && (
            <Button variant="outline" onClick={() => emailAll.mutate(run.id)} disabled={emailAll.isPending}>
              <Mail className="mr-2 h-4 w-4" />{emailAll.isPending ? 'Queueing…' : 'Email all payslips'}
            </Button>
          )}
          {run.status === 'APPROVED' && (
            <Button onClick={() => markPaid.mutate(run.id)} disabled={markPaid.isPending}>
              <BadgeCheck className="mr-2 h-4 w-4" />Mark as paid
            </Button>
          )}
          {(isDraft || run.status === 'REVIEW') && (
            <Button variant="outline" onClick={() => setCancelOpen(true)}>
              <Ban className="mr-2 h-4 w-4" />Cancel
            </Button>
          )}
        </>
      }
    >
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Badge variant="outline" className={`border-0 font-medium ${PAYROLL_RUN_STATUS_STYLES[run.status] ?? ''}`}>
          {run.status === 'REVIEW' ? 'IN REVIEW' : run.status}
        </Badge>
        {!isDraft && run.status !== 'CANCELLED' && (
          <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
            <Lock className="h-3 w-3" />Figures frozen
          </span>
        )}
        <span className="text-xs text-muted-foreground">
          {formatDateUTC(run.periodStart)} – {formatDateUTC(run.periodEnd)} · pay date {formatDateUTC(run.payDate)}
        </span>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        <Stat label="Gross" value={fmt(run.totalGross)} />
        <Stat label="Deductions" value={`− ${fmt(run.totalDeductions)}`} />
        <Stat label="Employer cost" value={fmt(run.totalEmployerCost)} />
        <Stat label="Net payable" value={fmt(run.totalNetPay)} highlight />
      </div>

      {/* What the approval actually posted. */}
      {run.expense && (
        <Card className="mt-4 border-sky-500/40 bg-sky-500/5">
          <CardContent className="flex flex-wrap items-center justify-between gap-3 pt-6">
            <div>
              <p className="text-xs text-muted-foreground">Posted to the ledger</p>
              <p className="text-sm font-medium">
                {run.expense.title} · {fmt(run.expense.amount)}
                {run.account && <span className="text-muted-foreground"> from {run.account.name}</span>}
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => navigate(`/expenses/${run.expense?.id}`)}>
              View expense<ExternalLink className="ml-2 h-3.5 w-3.5" />
            </Button>
          </CardContent>
        </Card>
      )}

      <Card className="mt-6">
        <CardHeader>
          <CardTitle className="text-base">Payslips</CardTitle>
          <p className="text-sm text-muted-foreground">
            {isDraft
              ? 'Adjust attendance and add one-off lines. Totals update as you go.'
              : 'These figures are frozen. Reopen the run to change anything.'}
          </p>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Employee</TableHead>
                <TableHead className="w-32">Paid days</TableHead>
                <TableHead className="text-right">Gross</TableHead>
                <TableHead className="text-right">Deductions</TableHead>
                <TableHead className="text-right">Net pay</TableHead>
                <TableHead>Adjustments</TableHead>
                {isDraft && <TableHead className="w-24" />}
              </TableRow>
            </TableHeader>
            <TableBody>
              {payslips.map((p) => (
                <TableRow key={p.id}>
                  <TableCell>
                    <button
                      type="button"
                      className="text-left hover:underline"
                      onClick={() => navigate(`/people/payslips/${p.id}`)}
                    >
                      <p className="font-medium">{p.employeeName}</p>
                      <p className="text-xs text-muted-foreground">{p.employeeCode} · {p.payslipNumber}</p>
                    </button>
                  </TableCell>
                  <TableCell>
                    {isDraft ? (
                      <PaidDaysInput
                        payslip={p}
                        onCommit={(paidDays) => updatePayslip.mutate({ payslipId: p.id, data: { paidDays } })}
                        disabled={updatePayslip.isPending}
                      />
                    ) : (
                      <span className="tabular-nums">{Number(p.paidDays)}/{p.totalDays}</span>
                    )}
                    {Number(p.lopDays) > 0 && (
                      <p className="text-xs text-amber-600 dark:text-amber-400">−{Number(p.lopDays)} LOP</p>
                    )}
                  </TableCell>
                  <TableCell className="text-right tabular-nums">{fmt(p.grossEarnings)}</TableCell>
                  <TableCell className="text-right tabular-nums">− {fmt(p.totalDeductions)}</TableCell>
                  <TableCell className="text-right font-medium tabular-nums">{fmt(p.netPay)}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(p.adjustments ?? []).map((a) => (
                        <Badge
                          key={a.id}
                          variant="outline"
                          className={`text-[10px] ${a.type === 'DEDUCTION' ? 'text-red-600 dark:text-red-400' : 'text-emerald-600 dark:text-emerald-400'}`}
                        >
                          {a.label} {a.type === 'DEDUCTION' ? '−' : '+'}{fmt(a.amount)}
                          {isDraft && (
                            <button
                              type="button"
                              className="ml-1 hover:text-destructive"
                              aria-label={`Remove ${a.label}`}
                              onClick={() => removeAdjustment.mutate({ payslipId: p.id, adjustmentId: a.id })}
                            >
                              <X className="h-2.5 w-2.5" />
                            </button>
                          )}
                        </Badge>
                      ))}
                      {isDraft && (
                        <Button variant="ghost" size="sm" className="h-6 px-2 text-xs" onClick={() => setAdjustTarget(p)}>
                          <Plus className="mr-1 h-3 w-3" />Add
                        </Button>
                      )}
                    </div>
                  </TableCell>
                  {isDraft && (
                    <TableCell>
                      <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => setExcludeTarget(p)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  )}
                </TableRow>
              ))}
              {payslips.length === 0 && (
                <TableRow>
                  <TableCell colSpan={isDraft ? 7 : 6} className="py-6 text-center text-sm text-muted-foreground">
                    No payslips in this run.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <AdjustmentDialog
        payslip={adjustTarget}
        currency={run.currency}
        onClose={() => setAdjustTarget(null)}
        isPending={addAdjustment.isPending}
        onSave={(data) => {
          if (adjustTarget) {
            addAdjustment.mutate({ payslipId: adjustTarget.id, data }, { onSuccess: () => setAdjustTarget(null) });
          }
        }}
      />

      <ApproveDialog
        open={approveOpen}
        onOpenChange={setApproveOpen}
        currency={run.currency}
        total={run.totalNetPay}
        employeeCount={run.employeeCount}
        periodLabel={periodLabel}
        isPending={approve.isPending}
        onApprove={(accountId) => approve.mutate({ id: run.id, data: { accountId } }, { onSuccess: () => setApproveOpen(false) })}
      />

      <ConfirmDialog
        open={!!excludeTarget}
        onOpenChange={() => setExcludeTarget(null)}
        title="Remove from this run"
        description={`${excludeTarget?.employeeName}'s draft payslip will be removed from ${run.runNumber}. They can be paid separately or in a later run.`}
        variant="destructive"
        confirmLabel="Remove"
        onConfirm={() => { if (excludeTarget) { excludePayslip.mutate(excludeTarget.id); setExcludeTarget(null); } }}
        isLoading={excludePayslip.isPending}
      />

      <ConfirmDialog
        open={cancelOpen}
        onOpenChange={setCancelOpen}
        title="Cancel Payroll Run"
        description={`${run.runNumber} will be cancelled and its draft payslips removed, freeing ${periodLabel} to be run again. Nothing has been posted to the ledger.`}
        variant="destructive"
        confirmLabel="Cancel run"
        onConfirm={() => { cancel.mutate(run.id); setCancelOpen(false); }}
        isLoading={cancel.isPending}
      />
    </PageContainer>
  );
}

function Stat({ label, value, highlight }: { label: string; value: string; highlight?: boolean }) {
  return (
    <Card className={highlight ? 'border-primary/40 bg-primary/5' : undefined}>
      <CardContent className="pt-6">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="mt-1 text-xl font-semibold tabular-nums">{value}</p>
      </CardContent>
    </Card>
  );
}

/**
 * Commits on blur or Enter rather than on every keystroke — each change is a
 * server round-trip that recomputes the payslip and the whole run.
 */
function PaidDaysInput({
  payslip, onCommit, disabled,
}: {
  payslip: Payslip;
  onCommit: (paidDays: number) => void;
  disabled?: boolean;
}) {
  const [value, setValue] = useState(String(Number(payslip.paidDays)));

  const commit = () => {
    const next = Number(value);
    if (!Number.isFinite(next) || next === Number(payslip.paidDays)) {
      setValue(String(Number(payslip.paidDays)));
      return;
    }
    if (next < 0 || next > payslip.totalDays) {
      setValue(String(Number(payslip.paidDays)));
      return;
    }
    onCommit(next);
  };

  return (
    <div className="flex items-center gap-1">
      <DecimalInput
        className="h-8 w-16"
        value={value}
        disabled={disabled}
        onChange={(e) => setValue(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
      />
      <span className="text-xs text-muted-foreground">/{payslip.totalDays}</span>
    </div>
  );
}

function AdjustmentDialog({
  payslip, currency, onClose, onSave, isPending,
}: {
  payslip: Payslip | null;
  currency: string;
  onClose: () => void;
  onSave: (data: { label: string; type: string; amount: number; notes?: string }) => void;
  isPending: boolean;
}) {
  const [label, setLabel] = useState('');
  const [type, setType] = useState('EARNING');
  const [amount, setAmount] = useState('');

  const reset = () => { setLabel(''); setType('EARNING'); setAmount(''); };
  const close = () => { reset(); onClose(); };
  const valid = label.trim().length > 0 && Number(amount) > 0;

  return (
    <Dialog open={!!payslip} onOpenChange={(v) => !v && close()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Add an adjustment</DialogTitle>
          <DialogDescription>
            A one-off line on {payslip?.employeeName}&apos;s payslip for this month only — arrears, a
            bonus, an advance recovery. It never touches their salary structure and is not prorated.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="adjLabel">Label <span className="text-destructive">*</span></Label>
            <Input id="adjLabel" value={label} onChange={(e) => setLabel(e.target.value)} placeholder="Arrears (Sep raise)" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="adjType">Type</Label>
              <Select value={type} onValueChange={setType}>
                <SelectTrigger id="adjType"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="EARNING">Earning (adds to pay)</SelectItem>
                  <SelectItem value="DEDUCTION">Deduction (subtracts)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="adjAmount">Amount <span className="text-destructive">*</span></Label>
              <DecimalInput id="adjAmount" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="25000" />
              <p className="text-xs text-muted-foreground">
                Always positive — the type decides the direction.
              </p>
            </div>
          </div>
          {Number(amount) > 0 && (
            <p className="text-sm">
              {type === 'DEDUCTION' ? 'Subtracts' : 'Adds'}{' '}
              <span className="font-medium tabular-nums">{formatCurrency(Number(amount), currency)}</span>
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={close}>Cancel</Button>
          <Button
            disabled={!valid || isPending}
            onClick={() => onSave({ label: label.trim(), type, amount: Number(amount) })}
          >
            {isPending ? 'Adding…' : 'Add adjustment'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ApproveDialog({
  open, onOpenChange, currency, total, employeeCount, periodLabel, onApprove, isPending,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  currency: string;
  total: string;
  employeeCount: number;
  periodLabel: string;
  onApprove: (accountId: string) => void;
  isPending: boolean;
}) {
  const { accounts } = useAccounts();
  const [accountId, setAccountId] = useState('');

  // Only accounts in the run's currency can take the posting.
  const payable = accounts.filter((a) => a.currency === currency && a.isActive !== false);
  const chosen = payable.find((a) => a.id === accountId);
  const insufficient = chosen ? Number(chosen.balance) < Number(total) : false;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Approve payroll for {periodLabel}</DialogTitle>
          <DialogDescription>
            This publishes {employeeCount} payslip{employeeCount === 1 ? '' : 's'} and posts one expense
            of <strong>{formatCurrency(total, currency)}</strong> to the ledger. Published payslips can
            no longer be edited, and an approved run cannot be reopened.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label>Pay from account <span className="text-destructive">*</span></Label>
            <Select value={accountId} onValueChange={setAccountId}>
              <SelectTrigger><SelectValue placeholder={`Select a ${currency} account`} /></SelectTrigger>
              <SelectContent>
                {payable.map((a) => (
                  <SelectItem key={a.id} value={a.id}>
                    {a.name} — {formatCurrency(a.balance, a.currency)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {payable.length === 0 && (
              <p className="text-xs text-destructive">
                No active {currency} account exists. Create one before approving.
              </p>
            )}
          </div>

          {insufficient && (
            <div className="flex gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3">
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
              <p className="text-xs text-amber-700 dark:text-amber-400">
                {chosen?.name} holds {formatCurrency(chosen?.balance ?? 0, currency)}, less than the
                {' '}{formatCurrency(total, currency)} payable. The posting will take the balance negative.
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={!accountId || isPending} onClick={() => onApprove(accountId)}>
            {isPending ? 'Posting…' : 'Approve & post to ledger'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
